<template>
  <div class="price-slider-container">
    <div class="price-range-label">
      Price, $
    </div>
    <div class="price-range-values">
      {{ formatMinPrice(minValue, min, noMinimum) }} – {{ formatMaxPrice(maxValue, max, noMaximum) }}
    </div>
    <div class="slider-container">
      <div class="slider-track" />
      <div
        class="slider-fill"
        :style="{ left: `${fillLeftPosition}%`, width: `${fillWidth}%` }"
      />

      <!-- Min thumb -->
      <div
        class="slider-thumb min-thumb"
        :style="{ left: `${minPosition}%` }"
        @mousedown="startDrag($event, 'min')"
      />

      <!-- Max thumb -->
      <div
        class="slider-thumb max-thumb"
        :style="{ left: `${maxPosition}%` }"
        @mousedown="startDrag($event, 'max')"
      />
    </div>
  </div>
</template>

<script>

import { formatMaxPrice, formatMinPrice, formatPrice } from '@/views/apps/retailer-ai/utils'

export default {
  name: 'PriceRangeSlider',
  props: {
    min: {
      type: Number,
      default: 0,
    },
    max: {
      type: Number,
      default: 200,
    },
    modelValue: {
      type: Array,
      default: () => [0, 200],
    },
    noMinimum: {
      type: Boolean,
      default: true,
    },
    noMaximum: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      minValue: this.modelValue[0],
      maxValue: this.modelValue[1],
      isDragging: false,
      currentThumb: null,
    }
  },
  computed: {
    minPosition() {
      return ((this.minValue - this.min) / (this.max - this.min)) * 100
    },
    maxPosition() {
      return ((this.maxValue - this.min) / (this.max - this.min)) * 100
    },
    fillLeftPosition() {
      return this.minPosition
    },
    fillWidth() {
      return this.maxPosition - this.minPosition
    },
    isAtMinValue() {
      return this.minValue <= this.min
    },
    isAtMaxValue() {
      return this.maxValue >= this.max
    },
  },
  mounted() {
    window.addEventListener('mousemove', this.onDrag)
    window.addEventListener('mouseup', this.stopDrag)
    window.addEventListener('touchmove', this.onDrag)
    window.addEventListener('touchend', this.stopDrag)
  },
  beforeDestroy() {
    window.removeEventListener('mousemove', this.onDrag)
    window.removeEventListener('mouseup', this.stopDrag)
    window.removeEventListener('touchmove', this.onDrag)
    window.removeEventListener('touchend', this.stopDrag)
  },
  methods: {
    formatPrice,
    formatMinPrice,
    formatMaxPrice,
    startDrag(event, thumb) {
      this.isDragging = true
      this.currentThumb = thumb
      event.preventDefault() // Prevent text selection during dragging
    },
    stopDrag() {
      if (!this.isDragging) return

      this.isDragging = false
      this.currentThumb = null

      const minVal = this.isAtMinValue && this.noMinimum ? null : this.minValue
      const maxVal = this.isAtMaxValue && this.noMaximum ? null : this.maxValue

      this.$emit('update:modelValue', [minVal, maxVal])
      this.$emit('change', [minVal, maxVal])
    },
    onDrag(event) {
      if (!this.isDragging) return

      const rect = this.$el.querySelector('.slider-container').getBoundingClientRect()
      const clientX = event.touches ? event.touches[0].clientX : event.clientX

      const percentage = Math.min(Math.max(0, (clientX - rect.left) / rect.width), 1)
      const newValue = Math.round(this.min + percentage * (this.max - this.min))

      if (this.currentThumb === 'min') {
        this.updateMinValue(newValue)
      } else if (this.currentThumb === 'max') {
        this.updateMaxValue(newValue)
      }
    },
    updateMinValue(newValue) {
      if (newValue >= this.min && newValue <= this.maxValue - 10) {
        this.minValue = newValue
        return
      }
      if (newValue < this.min) {
        this.minValue = this.min
      }
    },
    updateMaxValue(newValue) {
      if (newValue <= this.max && newValue >= this.minValue + 10) {
        this.maxValue = newValue
        return
      }
      if (newValue > this.max) {
        this.maxValue = this.max
      }
    },
  },
}
</script>

<style scoped lang="scss">
.price-slider-container {
  padding: 10px 0;
  width: 100%;
}

.price-range-label {
  font-weight: 700;
  font-size: 14px;
  line-height: 21px;
  margin-bottom: 4px;
}

.price-range-values {
  font-size: 14px;
  line-height: 21px;
  margin-bottom: 8px;
  color: #000;
}

.slider-container {
  position: relative;
  height: 4px;
  width: 100%;
  margin: 16px 0;
}

.slider-track {
  position: absolute;
  width: 100%;
  height: 4px;
  background-color: #E0E0E0;
  border-radius: 4px;
}

.slider-fill {
  position: absolute;
  height: 4px;
  background-color: #292929;;
  border-radius: 4px;
}

.slider-thumb {
  position: absolute;
  width: 20px;
  height: 20px;
  background-color: #292929;;
  border: 2px solid #FFF;
  border-radius: 50%;
  transform: translate(-50%, -8px);
  cursor: pointer;
  z-index: 2;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);

  &:hover {
    box-shadow: 0 1px 5px rgba(0, 0, 0, 0.4);
  }
}
</style>
